import React from 'react'
import Plot from '~/prix/react/components/plot'
import TimeSeriesPreview from '~/components/timeSeriesPreview'
import {
  stripParens,
  formatPercent,
  formatVisualizationValue,
  resolveVisualizationUnitFormat,
} from '../utils/format'
import { axisBase, buildDayOfMonthTicks, buildDayOfYearTicks } from '../utils/axis'
import {
  parseISO,
  getYear,
  getMonth,
  startOfYear,
  startOfMonth,
  addDays,
  isLeapYear,
  format as formatDateFns,
} from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  TimeSeriesComparisonProps,
  TimeSeriesComparisonPayload,
  SeriesInput,
  SeriesNormalized,
} from '../visualization.types'

import { computeChange } from '../utils/metrics'
import { Wrapper, MetricsRow, MetricValueWrap } from '../styles'
import { readMeta, resolveSeriesYear } from '../utils/extract'

type MetricItem = { label: 'MoM' | 'YoY' | 'YTD'; value: number | null }

export default function TimeSeriesComparison({
  visualization,
  isExpanded,
  isMaximized,
  activeButton,
  activeLabel,
}: TimeSeriesComparisonProps) {
  const payload = visualization?.payload as TimeSeriesComparisonPayload
  const rawSeries: SeriesInput[] = Array.isArray(payload.series) ? payload.series : []

  const chartHeight = !isExpanded ? 60 : isMaximized ? 250 : 205

  const series: SeriesNormalized[] = rawSeries.map((s, i) => ({
    name: stripParens(s.name ?? s.label) ?? `Série ${i + 1}`,
    color: s.color ?? '#2563eb',
    fillColor: s.fillColor,
    values: (s.y ?? []) as number[],
    year: s.year as number | undefined,
    labelRaw: (s.label ?? s.name ?? '') as string,
  }))

  // métrica compacta
  const change = computeChange(payload, series)
  const metricValues: MetricItem[] = change
    ? [
        {
          label: change.kind as MetricItem['label'],
          value: isFinite(change.percentChange) ? change.percentChange / 100 : null,
        },
      ]
    : []

  const refISO: string =
    payload?.meta?.reference?.start ||
    payload?.reference?.start ||
    new Date().toISOString().slice(0, 10)

  const comparisonISO: string | undefined =
    payload?.meta?.comparison?.start || payload?.comparison?.start

  const refDate = parseISO(refISO)
  const comparisonDate = comparisonISO ? parseISO(comparisonISO) : undefined
  const fallbackRefYear = getYear(refDate)
  const meta = readMeta(payload)
  const visualizationUnitFormat = resolveVisualizationUnitFormat(payload?.meta)

  if (!isExpanded) {
    const longestPreview = Math.max(...series.map(s => s.values?.length ?? 0)) || 1
    const isAnnualPreview = longestPreview >= 330
    return (
      <Wrapper $height={chartHeight}>
        <TimeSeriesPreview
          series={series}
          disableYAxis
          disablePoints
          padding={isAnnualPreview ? 8 : 0}
        />
      </Wrapper>
    )
  }

  // base para séries não anuais
  const refMonth0 = getMonth(refDate)
  const comparisonMonth0 = comparisonDate ? getMonth(comparisonDate) : refMonth0

  // eixo X
  const longest = Math.max(...series.map(s => s.values?.length ?? 0)) || 1
  const x = Array.from({ length: longest }, (_, i) => i + 1)
  const isAnnual = longest >= 330

  // ticks X
  let xTickVals: number[] = []
  let xTickTexts: string[] = []
  let tickangle: number | undefined = 0
  let xMaxForRange = longest

  if (isAnnual) {
    const baseYear = meta.comparisonYear ?? meta.refYear ?? fallbackRefYear
    const totalDaysThisYear = isLeapYear(new Date(Date.UTC(baseYear, 0, 1))) ? 366 : 365
    const { vals, texts } = buildDayOfYearTicks(totalDaysThisYear)
    xTickVals = vals
    xTickTexts = texts
    tickangle = 0
    xMaxForRange = Math.max(longest, totalDaysThisYear)
  } else {
    const t = buildDayOfMonthTicks(longest)
    xTickVals = t.vals
    xTickTexts = t.texts
    tickangle = 0
  }

  const basePad = Math.max(0.5, Math.round(xMaxForRange * 0.02))
  const xPadDays = isAnnual ? Math.max(6, basePad) : basePad
  const xRange: [number, number] = [0.5 - xPadDays, Math.max(1, xMaxForRange) + 0.5 + xPadDays]

  const defaultActiveFromMeta: string | undefined = isAnnual
    ? undefined
    : payload?.meta?.labels?.reference

  const resolvedActiveGlobal: string | undefined = isAnnual
    ? undefined
    : (activeLabel ?? activeButton ?? defaultActiveFromMeta)

  const resolvedActiveNormalized: string | undefined = resolvedActiveGlobal
    ? (stripParens(String(resolvedActiveGlobal)) ?? undefined)
    : undefined

  const data: Partial<Plotly.PlotData>[] = series.map((s, si) => {
    const year = resolveSeriesYear(s, si, meta, fallbackRefYear)

    // Use local dates to avoid timezone shifting tooltips to the previous day/month.
    const baseMonth0 = si === 0 ? comparisonMonth0 : refMonth0
    const baseDate = isAnnual
      ? startOfYear(new Date(year, 0, 1))
      : startOfMonth(new Date(year, baseMonth0, 1))

    const xs = x.slice(0, s.values.length)

    const hovertext = s.values.map((yv: number, i: number) => {
      const day = addDays(baseDate, xs[i] - 1)
      const label = formatDateFns(day, 'dd/MM/yyyy', { locale: ptBR })
      return `Data: ${label}<br>Valor: ${formatVisualizationValue(yv, visualizationUnitFormat)}`
    })

    const isActiveSeries =
      Boolean(resolvedActiveNormalized) &&
      (() => {
        const target = resolvedActiveNormalized as string
        const labelNorm = stripParens(String(s.labelRaw)) ?? ''
        return (
          s.name === target ||
          labelNorm === target ||
          s.name?.includes(target) ||
          labelNorm.includes(target)
        )
      })()

    return {
      x: xs,
      y: s.values,
      type: 'scatter',
      mode: 'lines+markers',
      line: { color: s.color, width: isAnnual ? 3 : isActiveSeries ? 4 : 3 },
      marker: { size: isAnnual ? 5 : isActiveSeries ? 8 : 5 },
      name: s.name,
      cliponaxis: true,
      hovertext,
      hovertemplate: '%{hovertext}<extra></extra>',
    }
  })

  const layout: Partial<Plotly.Layout> = {
    height: chartHeight,
    margin: { l: 35, r: 24, t: 25, b: 10 },
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    showlegend: false,
    xaxis: {
      ...axisBase,
      showgrid: false,
      autorange: false,
      range: xRange,
      tickmode: 'array',
      tickvals: xTickVals,
      ticktext: xTickTexts,
      tickangle,
      title: '',
    },
    yaxis: { ...axisBase, title: '', nticks: 4, tickformat: '~s' },
    hovermode: 'closest',
    hoverlabel: {
      align: 'left',
      bgcolor: 'white',
      bordercolor: '#e5e7eb',
      font: { size: 13, color: '#111827', family: 'Figtree, sans-serif' },
    },
  }

  return (
    <div>
      {isExpanded && metricValues[0]?.value != null && (
        <MetricsRow>
          {metricValues.map(metric => {
            const variant: 'positive' | 'negative' | 'flat' =
              metric.value == null
                ? 'flat'
                : metric.value > 0
                  ? 'positive'
                  : metric.value < 0
                    ? 'negative'
                    : 'flat'
            return (
              <MetricValueWrap key={metric.label} $variant={variant}>
                {metric.label} {formatPercent(metric.value)}
              </MetricValueWrap>
            )
          })}
        </MetricsRow>
      )}

      <Wrapper $height={chartHeight}>
        <Plot
          data={data as Plotly.Data[]}
          layout={layout as Plotly.Layout}
          config={{ responsive: false }}
          style={{ width: '100%', height: chartHeight }}
          useResizeHandler={false}
        />
      </Wrapper>
    </div>
  )
}
